import { useEffect, useState } from 'react';

/** We have many hooks that append to the window.DIGIOH_API object, but we need to wait for the
 *  lightbox_speed.js script to load and initialize the DIGIOH_API object before we can use it.
 *
 *  This hook can be largely broken down into four parts:
 *
 *  1. Check if window.DIGIOH_API is already available, which will be the case on any page that isn't the first page a user lands on.
 *  2. Wait for the lightbox_speed.js script to be injected into the head by GTM by observing the head. Disconnect the observer once the script is found.
 *  3. Once the script is injected, wait for it to load.
 *  4. Once the script has loaded, poll every 100ms until window.DIGIOH_API is available.
 *
 *  @returns {boolean} isReady - true if window.DIGIOH_API is ready, false otherwise.
 */
export function useDigiohAPIReady(): boolean {
  const [lightBoxScript, setLightBoxScript] = useState<HTMLScriptElement | null>(null);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);

  // ===== Wait for script to get injected into the head by GTM =====
  useEffect(() => {
    if (window.DIGIOH_API) {
      setIsReady(true);
      return;
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const script = document.querySelector('script[src*="lightbox_speed.js"]');
          if (script) {
            setLightBoxScript(script as HTMLScriptElement);
            observer.disconnect();
          }
        }
      });
    });

    observer.observe(document.head, {
      childList: true,
    });

    return () => observer.disconnect();
  }, []);

  // ===== Once script exists, wait for script to load before beginning polling =====
  useEffect(() => {
    if (lightBoxScript) {
      lightBoxScript.async = true;
      lightBoxScript.onload = () => {
        setHasLoaded(true);
      };
    }
  }, [lightBoxScript]);

  // ===== Poll every 100ms until script execution makes DIGIOH_API available =====
  useEffect(() => {
    if (!hasLoaded) return;

    const interval = setInterval(() => {
      if (window.DIGIOH_API) {
        setIsReady(true);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [hasLoaded]);

  return isReady;
}
